import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateSafe } from '../../../utils/dateUtils';
import {
  Trash2,
  Crown,
  Shield,
  User,
  Mail,
  Calendar,
  AlertCircle,
  CheckCircle,
  Settings,
  Key
} from 'lucide-react';
import type { EmpresaListItem, UsuarioEmpresa } from '../../../types';
import { useActualizarUsuarioEmpresa, useDesasignarUsuario } from '../../../hooks/useUsuariosEmpresa';
import EditUsuarioEmpresaModal from './EditUsuarioEmpresaModal';

interface UsuariosEmpresaTableProps {
  empresa: EmpresaListItem;
  usuarios: UsuarioEmpresa[];
  selectedUsers: number[];
  onSelectedUsersChange: (users: number[]) => void;
  loading: boolean;
}

const UsuariosEmpresaTable: React.FC<UsuariosEmpresaTableProps> = ({
  empresa,
  usuarios,
  selectedUsers,
  onSelectedUsersChange,
  loading
}) => {
  const { t } = useTranslation();
  const [editingUser, setEditingUser] = useState<UsuarioEmpresa | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  
  // Mutations
  const actualizarUsuario = useActualizarUsuarioEmpresa();
  const desasignarUsuario = useDesasignarUsuario();

  const handleSelectUser = (userId: number) => {
    if (selectedUsers.includes(userId)) {
      onSelectedUsersChange(selectedUsers.filter(id => id !== userId));
    } else {
      onSelectedUsersChange([...selectedUsers, userId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === usuarios.length) {
      onSelectedUsersChange([]);
    } else {
      onSelectedUsersChange(usuarios.map(u => u.usuario_id));
    }
  };

  const handleEditUser = (usuario: UsuarioEmpresa) => {
    setEditingUser(usuario);
  };

  const handleDeleteUser = (userId: number) => {
    setShowDeleteConfirm(userId);
  };

  const confirmDeleteUser = async () => {
    if (!showDeleteConfirm) return;
    
    try {
      await desasignarUsuario.mutateAsync({
        empresaId: empresa.id,
        usuarioId: showDeleteConfirm
      });
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error al desasignar usuario:', error);
    }
  };

  const handleToggleActive = async (usuario: UsuarioEmpresa) => {
    try {
      await actualizarUsuario.mutateAsync({
        empresaId: empresa.id,
        usuarioId: usuario.usuario_id,
        datos: {
          es_activa: !usuario.es_activa
        }
      });
    } catch (error) {
      console.error('Error al cambiar estado del usuario:', error);
    }
  };

  const getRolIcon = (rol: string) => {
    switch (rol) {
      case 'administrador':
        return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'supervisor':
        return <Shield className="h-4 w-4 text-blue-500" />;
      default:
        return <User className="h-4 w-4 text-gray-500" />;
    }
  };

  const getRolBadge = (rol: string) => {
    const configs = {
      administrador: 'bg-yellow-100 text-yellow-800',
      supervisor: 'bg-blue-100 text-blue-800',
      operador: 'bg-gray-100 text-gray-800'
    };
    
    return configs[rol as keyof typeof configs] || configs.operador;
  };

  const getPermisosCount = (usuario: UsuarioEmpresa) => {
    const permisos = usuario.permisos;
    const total = Object.keys(permisos).length;
    const activos = Object.values(permisos).filter(Boolean).length;
    return { activos, total };
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">{t('common.loading')}</p>
      </div>
    );
  }

  if (usuarios.length === 0) {
    return (
      <div className="p-8 text-center">
        <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t('common.no_data')}
        </h3>
        <p className="text-gray-600 mb-4">
          Esta empresa aún no tiene usuarios asignados.
        </p>
        <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium">
          {t('common.add')} usuario
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {/* Header */}
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedUsers.length === usuarios.length && usuarios.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuario
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rol
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Permisos
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha Asignación
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>

          {/* Body */}
          <tbody className="bg-white divide-y divide-gray-200">
            {usuarios.map((usuario) => {
              const permisosCount = getPermisosCount(usuario);
              const isSelected = selectedUsers.includes(usuario.usuario_id);
              
              return (
                <tr 
                  key={usuario.usuario_id}
                  className={`hover:bg-gray-50 transition-colors ${
                    isSelected ? 'bg-green-50' : ''
                  } ${
                    !usuario.es_activa ? 'opacity-60' : ''
                  }`}
                >
                  {/* Checkbox */}
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelectUser(usuario.usuario_id)}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                  </td>

                  {/* Usuario Info */}
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-green-600" />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900">
                            {usuario.usuario_info.nombre_completo || usuario.usuario_info.username}
                          </p>
                          {usuario.es_empresa_principal && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              Principal
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <Mail className="h-3 w-3" />
                          <span>{usuario.usuario_info.email}</span>
                        </div>
                        <p className="text-xs text-gray-500">
                          @{usuario.usuario_info.username}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Rol */}
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {getRolIcon(usuario.rol_en_empresa)}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getRolBadge(usuario.rol_en_empresa)
                      }`}>
                        {usuario.rol_en_empresa}
                      </span>
                    </div>
                  </td>

                  {/* Permisos */}
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Key className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {permisosCount.activos}/{permisosCount.total}
                      </span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full transition-all"
                          style={{ 
                            width: `${(permisosCount.activos / permisosCount.total) * 100}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </td>

                  {/* Estado */}
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleActive(usuario)}
                      disabled={actualizarUsuario.isLoading}
                      className="flex items-center space-x-1 text-sm transition-colors disabled:opacity-50"
                    >
                      {usuario.es_activa ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-green-700">{t('common.active')}</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-4 w-4 text-red-500" />
                          <span className="text-red-700">{t('common.inactive')}</span>
                        </>
                      )}
                    </button>
                  </td>

                  {/* Fecha Asignación */}
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-1 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {formatDateSafe(usuario.fecha_asignacion)}
                      </span>
                    </div>
                  </td>

                  {/* Acciones */}
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEditUser(usuario)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Editar permisos"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => handleDeleteUser(usuario.usuario_id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Desasignar usuario"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal de edición */}
      {editingUser && (
        <EditUsuarioEmpresaModal
          usuario={editingUser}
          empresa={empresa}
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => setEditingUser(null)}
        />
      )}

      {/* Confirmación de eliminación */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <AlertCircle className="h-6 w-6 text-red-500" />
              <h3 className="text-lg font-medium text-gray-900">
                {t('common.confirm_delete_title')}
              </h3>
            </div>

            <p className="text-gray-600 mb-6">
              {t('common.confirm_delete_msg')}
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmDeleteUser}
                disabled={desasignarUsuario.isLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50"
              >
                {desasignarUsuario.isLoading ? `${t('common.loading')}` : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UsuariosEmpresaTable;
